/** #999 — exclusive engine stall sync (never GetTransportInfo). */

import { diagnosticLog } from '../utils/diagnosticLogger';
import { evaluateExclusiveEngineTransportTick } from './exclusiveEngineTransportSync';
import { handleExclusivePlaybackStartFailed } from './localNativePositionStall';

type ConnectorModule = typeof import('../lib/connector');

export async function reconcileExclusiveEngineOnStall(
  frozenMs: number,
  getConnector: () => Promise<ConnectorModule>,
): Promise<void> {
  try {
    const C = await getConnector();
    const state = await C.exclusiveGetState();
    const tick = evaluateExclusiveEngineTransportTick(state, false);

    diagnosticLog('info', 'playback', '[PositionReconcile] exclusive engine stall sync', {
      frozenSecs: Math.round(frozenMs / 1000),
      engineEnded: state.ended === true,
      shouldAdvance: tick.shouldAdvance,
    });

    const store = (await import('../store/maestroStore')).useMaestroStore.getState();
    const frozenSecs = Math.round(frozenMs / 1000);
    const position = store.position || 0;
    const engineStuckAtStart =
      (state.is_playing || store.playbackState === 'playing')
      && position < 1
      && state.position_secs < 1
      && !state.ended;

    if (engineStuckAtStart && frozenSecs >= 5) {
      await handleExclusivePlaybackStartFailed(
        store.activeConnectorRenderer?.id || '',
        'Playback did not advance on this output — no audio reached the device.',
        true,
      );
      return;
    }

    if (tick.shouldAdvance) {
      const { QueueController } = await import('./QueueController');
      await QueueController.advanceTrackEnd({ trustExclusiveEngineEnd: true });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn('[PositionReconcile] exclusiveGetState failed:', message);
  }
}

export type ExclusiveStallTickInput = {
  playbackState: string;
  position: number;
  lastObservedPosition: number;
  lastPositionChangeAt: number;
  stallReconcileCooldownUntil: number;
  stallReconcileMs: number;
  nowMs: number;
};

let exclusiveStallReconcileInFlight = false;

export function isExclusiveTransportInfoSkipReason(
  reason: string,
): boolean {
  return reason === 'exclusive-active'
    || reason === 'exclusive-engine-playing'
    || reason === 'local-native-zone'
    || reason === 'local-native-renderer';
}

export async function reconcileExclusiveInsteadOfTransportInfo(
  frozenMs: number,
  getConnector: () => Promise<ConnectorModule>,
  armCooldown: (untilMs: number) => void,
  cooldownMs: number,
  input: { exclusiveActive: boolean; skipReason?: string },
): Promise<boolean> {
  const viaSkip = input.skipReason && isExclusiveTransportInfoSkipReason(input.skipReason);
  if (!input.exclusiveActive && !viaSkip) return false;
  await runExclusiveStallReconcile(frozenMs, getConnector, armCooldown, cooldownMs);
  return true;
}

export async function runExclusiveStallReconcile(
  frozenMs: number,
  getConnector: () => Promise<ConnectorModule>,
  armCooldown: (untilMs: number) => void,
  cooldownMs: number,
): Promise<void> {
  if (exclusiveStallReconcileInFlight) return;
  exclusiveStallReconcileInFlight = true;
  armCooldown(Date.now() + cooldownMs);
  try {
    await reconcileExclusiveEngineOnStall(frozenMs, getConnector);
  } finally {
    exclusiveStallReconcileInFlight = false;
  }
}

/** Returns frozenMs when an exclusive-engine stall reconcile should run. */
export function exclusiveStallFrozenMs(input: ExclusiveStallTickInput): number | null {
  if (input.playbackState !== 'playing') return null;

  const pos = input.position || 0;
  if (pos !== input.lastObservedPosition) return null;

  const frozenMs = input.nowMs - input.lastPositionChangeAt;
  if (frozenMs < input.stallReconcileMs || input.nowMs < input.stallReconcileCooldownUntil) {
    return null;
  }
  return frozenMs;
}
